/**
 * =============================================================================
 * FeatureGuard.tsx - Componente guardia per feature flag
 * =============================================================================
 * 
 * Renderizza i children solo se il feature flag specificato è abilitato
 * per il tenant corrente. Utilizzabile sia come wrapper UI che come
 * guardia per le route.
 * 
 * USO IN MENU/SIDEBAR:
 *   <FeatureGuard flag="feature_quiz_engine">
 *     <Link to="/quiz">Quiz</Link>
 *   </FeatureGuard>
 * 
 * USO IN ROUTE:
 *   <Route path="/quiz" element={
 *     <FeatureGuard flag="feature_quiz_engine" fallback={<Navigate to="/dashboard" />}>
 *       <QuizPage />
 *     </FeatureGuard>
 *   } />
 * 
 * @version 1.0.0 - Feature Flags Phase 2
 * =============================================================================
 */

import { ReactNode } from "react";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import type { FeatureFlag } from "@/config/featureFlags";

interface FeatureGuardProps {
  /** Il feature flag da verificare */
  flag: FeatureFlag;
  /** Contenuto da renderizzare se il flag è abilitato */
  children: ReactNode;
  /** Contenuto alternativo se il flag è disabilitato (default: null = nascosto) */
  fallback?: ReactNode;
}

/**
 * Componente che renderizza condizionalmente basandosi sui feature flag del tenant.
 * Se il flag non è abilitato, renderizza il fallback (o nulla).
 */
export function FeatureGuard({ flag, children, fallback = null }: FeatureGuardProps) {
  const isEnabled = useFeatureFlag(flag);
  
  if (!isEnabled) {
    return <>{fallback}</>;
  }
  
  return <>{children}</>;
}
